// client/src/components/map/LexiStarMap.js
// LexiStar shared map foundation (ST-00118, Phase 0). A thin, reusable wrapper
// around react-map-gl/maplibre that all LexiStar map surfaces (live tracking,
// trips, the zone editor) build on. It owns the basemap (street vs satellite),
// the map controls, and the Street/Satellite toggle look — nothing app-specific.
//
// Basemap swap preserves the camera (maplibre setStyle keeps center/zoom) and
// re-adds any declarative <Source>/<Layer>/<Marker> children automatically
// (react-map-gl re-injects them on the style-load event). Callers that manage
// imperative sources should also listen via onStyleData to re-apply data/images.
import React, { forwardRef, useCallback, useState } from 'react';
import { Map, NavigationControl, AttributionControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

// Street: OpenFreeMap "liberty" vector style (no API key, MapLibre style v8).
export const STREET_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

// Satellite: Esri World Imagery as a raster style. `maxzoom: 19` on the SOURCE
// (not the layer) tells MapLibre the deepest zoom native tiles exist at; past
// that it overzooms (upscales) the z19 tiles instead of requesting 404s, so the
// map stays usable up to the map's own maxZoom.
export const SATELLITE_STYLE = {
  version: 8,
  sources: {
    'esri-world-imagery': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution: 'Esri, Maxar, Earthstar Geographics',
    },
  },
  layers: [{ id: 'esri-world-imagery', type: 'raster', source: 'esri-world-imagery' }],
};

export const BASEMAPS = [
  { id: 'street', label: 'Street' },
  { id: 'satellite', label: 'Satellite' },
];

const POSITION_CLASSES = {
  'top-left': 'top-3 left-3',
  'top-right': 'top-3 right-3',
  'bottom-left': 'bottom-3 left-3',
  'bottom-right': 'bottom-3 right-3',
};

/**
 * Street/Satellite segmented toggle. Matches the in-map control look
 * (white/95 backdrop-blur rounded-lg shadow, teal-500 active state) so callers
 * can drop it into their own control clusters.
 */
export function BasemapToggle({ basemap, onChange, className = '' }) {
  return (
    <div
      className={`bg-surface/95 backdrop-blur rounded-lg shadow-lg flex overflow-hidden text-xs font-medium ${className}`}
      role="group"
      aria-label="Basemap"
    >
      {BASEMAPS.map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={() => onChange(b.id)}
          aria-pressed={basemap === b.id}
          className={`px-3 py-1.5 transition-colors ${
            basemap === b.id ? 'bg-teal-500 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {b.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Reusable LexiStar map.
 *
 * Basemap can be controlled (pass `basemap` + `onBasemapChange`) or left
 * uncontrolled (internal state seeded by `defaultBasemap`, with the built-in
 * toggle). Set `showBasemapToggle={false}` when the caller renders its own
 * toggle (e.g. inside a bottom-left control cluster).
 *
 * The ref is forwarded to react-map-gl's <Map>, but most imperative work is
 * easier off the raw maplibre instance via `onLoad(e => e.target)`.
 */
const LexiStarMap = forwardRef(function LexiStarMap(
  {
    initialViewState,
    maxZoom = 22,
    minZoom = 2,
    // basemap (controlled | uncontrolled)
    basemap: basemapProp,
    defaultBasemap = 'satellite',
    onBasemapChange,
    showBasemapToggle = true,
    basemapTogglePosition = 'bottom-left',
    // controls
    showNavigation = true,
    navigationPosition = 'top-right',
    compactAttribution = true,
    // interaction
    interactiveLayerIds,
    cursor: cursorProp,
    onLoad,
    onClick,
    onStyleData,
    onMouseEnter,
    onMouseLeave,
    onMoveEnd,
    // layout
    className,
    overlay = null,
    children,
    reuseMaps = true,
    ...rest
  },
  ref
) {
  const [internalBasemap, setInternalBasemap] = useState(defaultBasemap);
  const basemap = basemapProp ?? internalBasemap;
  const setBasemap = useCallback(
    (b) => {
      onBasemapChange?.(b);
      if (basemapProp == null) setInternalBasemap(b);
    },
    [onBasemapChange, basemapProp]
  );

  const mapStyle = basemap === 'satellite' ? SATELLITE_STYLE : STREET_STYLE_URL;

  // Pointer cursor over interactive features; otherwise let maplibre manage the
  // grab/grabbing drag cursor itself (pass cursor=undefined).
  const [hovering, setHovering] = useState(false);
  const handleMouseEnter = useCallback(
    (e) => {
      setHovering(true);
      onMouseEnter?.(e);
    },
    [onMouseEnter]
  );
  const handleMouseLeave = useCallback(
    (e) => {
      setHovering(false);
      onMouseLeave?.(e);
    },
    [onMouseLeave]
  );
  const cursor = cursorProp !== undefined ? cursorProp : hovering ? 'pointer' : undefined;

  return (
    <div className={className || 'relative w-full h-full'}>
      <Map
        ref={ref}
        initialViewState={initialViewState}
        mapStyle={mapStyle}
        maxZoom={maxZoom}
        minZoom={minZoom}
        reuseMaps={reuseMaps}
        dragRotate={false}
        touchPitch={false}
        pitchWithRotate={false}
        attributionControl={false}
        interactiveLayerIds={interactiveLayerIds}
        cursor={cursor}
        onLoad={onLoad}
        onClick={onClick}
        onStyleData={onStyleData}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMoveEnd={onMoveEnd}
        style={{ width: '100%', height: '100%' }}
        {...rest}
      >
        {showNavigation && (
          <NavigationControl
            position={navigationPosition}
            showCompass={false}
            visualizePitch={false}
          />
        )}
        {compactAttribution && <AttributionControl compact position="bottom-right" />}
        {children}
      </Map>

      {showBasemapToggle && (
        <div
          className={`absolute z-[1000] ${POSITION_CLASSES[basemapTogglePosition] || POSITION_CLASSES['bottom-left']}`}
        >
          <BasemapToggle basemap={basemap} onChange={setBasemap} />
        </div>
      )}

      {overlay}
    </div>
  );
});

export default LexiStarMap;
